import type { ReactNode } from 'react';

import { BrandAccent } from '#/components/ui/BrandAccent';
import { RichText } from '#/components/ui/RichText';
import type { UpdatedProps } from '#/components/ui/Updated';
import { Updated } from '#/components/ui/Updated';
import { cn } from '#/utils/cn';

export type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

interface SectionHeaderProps {
  title: ReactNode;
  actions?: ReactNode;
  accentVariant?: 'short' | 'long';
  /** Alignment for the actions that appear in upper right  */
  actionAlignment?: 'start' | 'center';
  /** Alignment for content  */
  contentAlignment?: 'start' | 'center';
  /** ISO 8601 timestamp for the "Last updated" stamp below the title. */
  date?: string | null;
  /** Whether the timestamp reads as a time or a date. */
  dateVariant?: UpdatedProps['variant'];
  /** Trusted HTML rendered full width below the title/actions row. */
  description?: string | null;
  /** Heading element for the card title. Pick for the document outline in descending order (h2 -> h3, etc) */
  headingLevel?: HeadingLevel;
  /** Classes for the <h1-6> element. */
  headingClassName?: string;
  /** Classes for the main wrapping <header>. */
  className?: string;
  children?: ReactNode;
}

export function SectionHeader({
  title,
  actions,
  accentVariant = 'short',
  actionAlignment = 'start',
  contentAlignment = 'start',
  date,
  dateVariant = 'time',
  description,
  headingLevel: Heading = 'h2',
  headingClassName,
  className,
  children,
}: SectionHeaderProps) {
  const rowClass = cn(
    'flex flex-col items-start justify-between md:flex-row',
    actionAlignment === 'center' ? 'md:items-center' : 'md:items-start',
  );
  const contentClass = cn(
    'flow flow-8 flex w-full min-w-0 flex-col md:flex-1',
    contentAlignment === 'center'
      ? 'md:items-center md:text-center'
      : 'md:items-start',
  );

  return (
    <header className={cn('flow flow-inner-8', className)}>
      <div className={rowClass}>
        <div className={contentClass}>
          <BrandAccent variant={accentVariant} />
          <Heading
            className={cn(
              'text-fluid-subheader font-semibold',
              headingClassName,
            )}
          >
            {title}
          </Heading>
          {date && <Updated date={date} variant={dateVariant} />}
          {children}
        </div>
        {actions}
      </div>
      {description && (
        <RichText
          html={description}
          className="text-fg text-fluid-base-sm w-full"
        />
      )}
    </header>
  );
}
